import { i18nTranslationHelper } from '@/i18n'
import { formatDateString, formatLocaleStartTime, formatTimeString } from '@/main/helpers'
import { Shift } from '@/domain/models/content/shift'
import { Plan } from '@/domain/models/plan-license/plan-model'
import {
  ProductStatesResponse,
  ProductCitiesResponse,
  ProductUnitsResponse,
  ProductUnitsResponseRaw,
  ProductAvailableOptionResponse,
  FormattedProductAvailableOptionResponse,
  ProductAvailableInfoRawResponse,
  ProductAvailableInfoResponse,
  ProductAvailableOptionOfferItem
} from './product-content-service-types'

// Instância de tradução local — usada no formatLocaleStartTime para localizar horários
const t = {
  components: (key: string, params?: Record<string, string>) =>
    i18nTranslationHelper.getTComponents(key, params ?? {})
}

/**
 * Filtra as ofertas que possuem pelo menos uma informação de agendamento visível ao usuário.
 *
 * Regra de visibilidade:
 * - `startAt` (data de início): só exibida quando NÃO está em modo ciclos.
 *   Em modo ciclos o usuário escolhe o período na etapa de ciclos, não na oferta.
 * - `startTime`, `shift`, `frequency`, `minuteClassDuration`: sempre exibidos, independente do modo.
 *
 * Ofertas sem nenhum campo visível são filtradas — elas ainda podem aparecer via extraInfo
 * (server double-check) sem precisar de um card visual na lista.
 */
export const filterOffersWithVisibleScheduleInfo = (
  offers: ProductAvailableOptionOfferItem[],
  isAvailableCycles: boolean
): ProductAvailableOptionOfferItem[] => {
  return offers.filter(
    offer =>
      (!isAvailableCycles && !!offer.startAt) ||
      !!offer.startTime ||
      !!offer.shift ||
      !!offer.frequency ||
      !!offer.minuteClassDuration
  )
}

// Pass-through: estados chegam como array de strings da API, sem transformação necessária.
// Mantido como função para consistência com o padrão do service (sempre formatar na camada de utils).
export const formatProductStates = (response: ProductStatesResponse): ProductStatesResponse => {
  return response
}

// Pass-through: cidades chegam como array de strings da API, sem transformação necessária.
export const formatProductCities = (response: ProductCitiesResponse): ProductCitiesResponse => {
  return response
}

/**
 * Transforma a resposta bruta de unidades em um formato plano para o wizard.
 *
 * Principais transformações:
 * - Acha o objeto `address` aninhado em uma string única de exibição:
 *   "rua, complemento, bairro - cidade/UF"
 * - Campos `null` ou vazios são filtrados pelo `.filter(Boolean)` antes do join
 * - `unit` usa o campo `name` da API como identificador único da unidade
 */
export const formatProductUnits = (response: ProductUnitsResponseRaw): ProductUnitsResponse => {
  return {
    ...response,
    items: response.items.map(unit => {
      return {
        state: unit.address.state,
        city: unit.address.city,
        unit: unit.name,
        // Partes opcionais do endereço (complemento, bairro) são omitidas quando vazias
        address: [unit.address.location, unit.address.complement, unit.address.neighborhood]
          .filter(Boolean)
          .join(', ')
          .concat(` - ${unit.address.city}/${unit.address.state}`)
      }
    })
  }
}

/**
 * Transforma as ofertas disponíveis do formato bruto da API para o formato do wizard.
 *
 * Transformações aplicadas a cada oferta:
 * - `startAt`: formatado de ISO string para data legível (ex: "Jan 2025")
 * - `startTime`: formatado com locale (ex: "19:00:00" → "19h")
 * - `shift`: traduzido pelo modelo Shift (ex: "NIGHT" → "Noturno"); fallback = valor original
 * - `minuteClassDuration`: convertido de número para string legível (ex: 90 → "1h30min")
 * - `plan`: resolvido de ID numérico para objeto Plan.Enum com nome, cor, etc.
 * - `enrollmentDeadline`: formatado de ISO string para data legível
 */
export const transformProductAvailableResponse = (
  response: ProductAvailableOptionResponse
): FormattedProductAvailableOptionResponse => ({
  ...response,
  items: response.items.map(item => ({
    offerId: item.offerId,
    startAt: formatDateString(item.startAt ?? undefined) ?? null,
    startTime: item.startTime ? formatLocaleStartTime(item.startTime, t) : undefined,
    shift: item.shift ? Shift.getByName(item.shift)?.title ?? item.shift : null,
    frequency: item.frequency ?? undefined,
    minuteClassDuration: item.minuteClassDuration
      ? formatTimeString(item.minuteClassDuration)
      : undefined,
    coveredByPlan: item.coveredByPlan,
    requiredPlanId: item.requiredPlanId,
    // Resolve o plano exigido a partir do ID — null quando o produto não exige plano específico
    plan: Plan.findById(item.requiredPlanId ?? null),
    workloadHours: item.workloadHours,
    monthlyDuration: item.monthlyDuration,
    additionalInfo: item.additionalInfo,
    enrollmentDeadline: item.enrollmentDeadline
      ? formatDateString(item.enrollmentDeadline)
      : undefined
  }))
})

/**
 * Transforma a resposta bruta de available-info, convertendo os IDs de plano
 * em objetos Plan.Enum com todas as informações necessárias para exibição (nome, cor, etc).
 *
 * IDs de plano inválidos (não encontrados no enum) são descartados pelo `.filter(Boolean)`.
 */
export const formatProductAvailableInfo = (
  data: ProductAvailableInfoRawResponse
): ProductAvailableInfoResponse => {
  return {
    ...data,
    requiredPlans: (data.requiredPlans ?? [])
      .map(planId => Plan.findById(planId))
      .filter(Boolean) as Plan.Enum[]
  }
}
